import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useInView, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { Helmet } from 'react-helmet';
import heroImageDesktop from "./assets/mainPortrait2-2560.webp";
import heroImageMobile from "./assets/mainPortrait2-1280.webp";
import terminalVideo from "./assets/terminal.mp4";
import meme from "./assets/diamond-meme.jpg";
import { Github } from "lucide-react";
import ColorBends from "./components/ColorBends";
import FooterSection from "./components/FooterSection";
import RecentUpdatesSection from "./components/RecentUpdatesSection";
import "./index.css";
import CV from "./assets/DulainGunawardhanaCV.pdf";
import SEO from './components/SEO';
const navLinks = ["About", "Projects", "CV", "Posts"];
const SHOW_UNDER_CONSTRUCTION = true;

type ProjectCard = {
  name: string;
  repo: string;
  owner: string;
  description: string;
  image: string;
  stack: string[];
  link: string;
};

const featuredProjects: ProjectCard[] = [
  {
    name: "Kontrakt",
    repo: "Kontrakt",
    owner: "notdulain",
    description:
      "A Domain Specific Language (DSL) and compiler designed for robust Backend API Testing and validation.",
    image: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=1200&q=80&auto=format&fit=crop",
    stack: ["JFLEX", "CUP", "compiler-design", "dsl", "testing"],
    link: "https://github.com/notdulain/Kontrakt",
  },
  {
    name: "Wiber",
    repo: "wiber",
    owner: "notdulain",
    description:
      "A Python-based multi-node, fault‑tolerant messaging system with a FastAPI gateway and a simple web UI.",
    image: "https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=1200&q=80&auto=format&fit=crop",
    stack: ["python", "FastAPI", "RAFT", "Websockets", "Time-Synchronization"],
    link: "https://github.com/notdulain/wiber",
  },
];



export default function App() {
  const heroRef = useRef<HTMLElement | null>(null);
  const terminalRef = useRef<HTMLElement | null>(null);
  const projectsRef = useRef<HTMLElement | null>(null);
  const { scrollY } = useScroll();
  const heroInView = useInView(heroRef, { amount: 0.5 });

  const heroTextY = useTransform(scrollY, [0, 500], [0, 100]);
  const heroImageY = useTransform(scrollY, [0, 500], [0, 50]);
  const heroImageScale = useTransform(scrollY, [0, 500], [1, 1.1]);

  const { scrollYProgress: terminalProgress } = useScroll({
    target: terminalRef,
    offset: ["start end", "end start"],
  });
  const terminalVideoScale = useTransform(terminalProgress, [0, 0.6, 1], [1, 1, 1.08]);
  const terminalTextBlur = useTransform(terminalProgress, [0, 0.6, 1], ["0px", "0px", "8px"]);
  const terminalTextOpacity = useTransform(terminalProgress, [0, 0.6, 1], [1, 1, 0.25]);
  const terminalTextScale = useTransform(terminalProgress, [0, 0.6, 1], [1, 1, 1.05]);

  const { scrollYProgress: projectsProgress } = useScroll({
    target: projectsRef,
    offset: ["start end", "end start"],
  });
  const colorBendsScale = useTransform(projectsProgress, [0, 0.5, 1], [1, 1.05, 1.12]);

  const [repoStars, setRepoStars] = useState<Record<string, number>>({});
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showNavbar, setShowNavbar] = useState(true);
  const lastScrollY = useRef(0);

  //About page video pre-load
  useEffect(() => {
    // Wait a bit so we don't compete with critical page resources
    const timer = setTimeout(() => {
      fetch(
        "https://res.cloudinary.com/dhntips7e/video/upload/v1771338596/AboutPage-2_nxctur.mp4"
      )
        .then((res) => res.blob()) // Download the full video into browser cache
        .then(() => console.log("About video prefetched ✓"))
        .catch(() => { }); // Silently fail, it's just a prefetch
    }, 3000); // 3 second delay after mount

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      const scrollingDown = currentY > lastScrollY.current && currentY > 100;
      setShowNavbar(!scrollingDown);
      lastScrollY.current = currentY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);



  useEffect(() => {
    const controller = new AbortController();

    const loadStars = async () => {
      try {
        const responses = await Promise.all(
          featuredProjects.map(project =>
            fetch(`https://api.github.com/repos/${project.owner}/${project.repo}`, {
              signal: controller.signal,
            }).then(res => (res.ok ? res.json() : null)),
          ),
        );

        const nextStars: Record<string, number> = {};
        responses.forEach((data, index) => {
          if (data?.stargazers_count !== undefined) {
            const key = `${featuredProjects[index]?.owner}/${featuredProjects[index]?.repo}`;
            if (key) {
              nextStars[key] = data.stargazers_count as number;
            }
          }
        });
        setRepoStars(nextStars);
      } catch (error) {
        if ((error as Error).name !== "AbortError") {
          console.error("Failed to load GitHub stars", error);
        }
      }
    };

    loadStars();
    return () => controller.abort();
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setIsMenuOpen(false);
  };

  const showUnderConstruction = SHOW_UNDER_CONSTRUCTION && !heroInView;

  return (
    <div className="min-h-screen relative text-[#f6f1ea] overflow-hidden bg-[#0c0c0c]">
      <SEO />
      <Helmet>
        <link rel="preload" as="image" href={heroImageMobile} type="image/webp" media="(max-width: 767px)" />
        <link rel="preload" as="image" href={heroImageDesktop} type="image/webp" media="(min-width: 768px)" />
      </Helmet>
      {/* Navbar */}
      <div
        className={`fixed top-0 left-0 right-0 z-30 transition-transform duration-300 ${showNavbar ? "translate-y-0" : "-translate-y-24"
          }`}
      >
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-7 flex items-center justify-center relative">
          <div className="hidden md:flex items-center gap-12">
            {["About", "Projects", "CV", "More"].map(label => {
              if (label === "More") {
                return (
                  <button
                    key={label}
                    onClick={() => setIsMenuOpen(true)}
                    className="text-white text-xs md:text-sm uppercase tracking-[0.25em] font-light hover:text-[#ff5c7a] transition-colors"
                  >
                    {label}
                  </button>
                );
              }
              if (label === "CV") {
                return (
                  <a
                    key={label}
                    href={CV}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white text-xs md:text-sm uppercase tracking-[0.25em] font-light hover:text-white/70 transition-colors"
                  >
                    {label}
                  </a>
                );
              }
              if (label === "About") {
                return (
                  <Link
                    key={label}
                    to="/about"
                    className="text-white text-xs md:text-sm uppercase tracking-[0.25em] font-light hover:text-white/70 transition-colors"
                  >
                    {label}
                  </Link>
                )
              }
              return (
                <button
                  key={label}
                  onClick={() => document.getElementById(label.toLowerCase())?.scrollIntoView({ behavior: 'smooth' })}
                  className="text-white text-xs md:text-sm uppercase tracking-[0.25em] font-light hover:text-white/70 transition-colors"
                >
                  {label}
                </button>
              );
            })}
          </div>

          <button
            className="md:hidden flex flex-col justify-center gap-[5px] w-8 h-8 absolute right-6 top-1/2 -translate-y-1/2"
            onClick={() => setIsMenuOpen(true)}
            aria-label="Open menu"
          >
            <span className="block h-[1.5px] bg-white/80 w-6" />
            <span className="block h-[1.5px] bg-white/80 w-6" />
            <span className="block h-[1.5px] bg-white/80 w-6" />
          </button>
        </div>
      </div>

      {/* Cinematic Side Drawer */}
      <AnimatePresence>
        {isMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              className="fixed inset-0 bg-black/60 z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMenuOpen(false)}
            />

            {/* Drawer Container */}
            <motion.div
              className="fixed top-0 right-0 bottom-0 w-full md:w-[320px] z-50 p-12 flex flex-col items-center md:items-start"
              style={{
                background: window.innerWidth >= 768
                  ? "linear-gradient(to right, transparent 0%, rgba(12,12,12,0.3) 25%, rgba(12,12,12,0.7) 55%, rgba(12,12,12,0.95) 80%, #0c0c0c 100%)"
                  : "rgba(12,12,12,0.97)"
              }}
              initial={window.innerWidth >= 768 ? { x: "100%" } : { opacity: 0 }}
              animate={window.innerWidth >= 768 ? { x: 0 } : { opacity: 1 }}
              exit={window.innerWidth >= 768 ? { x: "100%" } : { opacity: 0 }}
              transition={window.innerWidth >= 768 ? { type: "spring", damping: 30, stiffness: 300 } : { duration: 0.2 }}
            >
              {/* Header */}
              <div className="flex justify-end mb-12 w-full">
                <button
                  className="text-white/50 hover:text-white transition-colors p-1"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>

              {/* Main Links */}
              <nav className="flex flex-col gap-8 md:items-start items-center">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1, duration: 0.3 }}
                >
                  <Link
                    to="/about"
                    onClick={() => setIsMenuOpen(false)}
                    className="text-white/60 hover:text-white text-sm font-light uppercase tracking-[0.35em] transition-all hover:pl-2"
                  >
                    About Me
                  </Link>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.3 }}
                >
                  <button
                    onClick={() => { setIsMenuOpen(false); projectsRef.current?.scrollIntoView({ behavior: 'smooth' }) }}
                    className="text-white/60 hover:text-white text-sm font-light uppercase tracking-[0.35em] transition-all hover:pl-2"
                  >
                    Projects
                  </button>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.3 }}
                >
                  <a
                    href={CV}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white/60 hover:text-white text-sm font-light uppercase tracking-[0.35em] transition-all hover:pl-2"
                  >
                    CV
                  </a>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.3 }}
                >
                  <Link
                    to="#"
                    className="text-white/60 hover:text-white text-sm font-light uppercase tracking-[0.35em] transition-all hover:pl-2 opacity-50 cursor-not-allowed"
                  >
                    Articles
                  </Link>
                </motion.div>
              </nav>

              {/* Divider */}
              <div className="h-px bg-white/25 w-full mt-10 mb-8 md:mb-2" />

              {/* More Section */}
              <div className="space-y-10 flex flex-col md:items-start items-center">
                <span className="text-[10px] font-light text-white/45 uppercase tracking-[0.35em] hidden md:block">More</span>
                <div className="flex flex-col gap-8 md:items-start items-center">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5, duration: 0.3 }}
                  >
                    <a
                      href="https://site.realdulain.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white/60 hover:text-white text-sm font-light uppercase tracking-[0.35em] transition-all hover:pl-2"
                    >
                      Terminal
                    </a>
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6, duration: 0.3 }}
                  >
                    <Link
                      to="#"
                      className="text-white/60 hover:text-white text-sm font-light uppercase tracking-[0.35em] transition-all hover:pl-2"
                    >
                      Pics
                    </Link>
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7, duration: 0.3 }}
                  >
                    <Link
                      to="#"
                      className="text-white/60 hover:text-white text-sm font-light uppercase tracking-[0.35em] transition-all hover:pl-2 opacity-50 cursor-not-allowed"
                    >
                      Research
                    </Link>
                  </motion.div>
                </div>
              </div>

              {/* Bottom Socials */}
              <div className="mt-auto flex justify-end gap-6 pt-8">
                <a
                  href="https://github.com/notdulain"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/40 hover:text-white transition-colors"
                >
                  <Github size={22} />
                </a>
                <a
                  href="https://www.linkedin.com/in/dulain-nethwin-gunawardhana-41b251294/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/40 hover:text-white transition-colors"
                >
                  <svg className="w-[22px] h-[22px]" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" /></svg>
                </a>
              </div>

            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* 1 - Hero  */}
      <section
        ref={heroRef}
        className="relative z-10 min-h-screen flex flex-col items-start justify-start px-4 md:px-6 overflow-hidden"
      >
        {/* Background image with soft grain behind hero */}
        <motion.div
          className="absolute inset-0 -z-10"
          style={{ y: heroImageY, scale: heroImageScale }}
        >
          <div className="relative h-full w-full">
            <picture>
              <source media="(max-width: 767px)" srcSet={heroImageMobile} type="image/webp" />
              <img
                src={heroImageDesktop}
                alt="Dulain portrait"
                width={2560}
                height={1440}
                loading="eager"
                fetchPriority="high"
                decoding="sync"
                className="absolute inset-0 h-full w-full object-cover object-[65%] md:object-center"
              />
            </picture>
            <div
              className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 via-[60%] to-transparent md:bg-none"
              style={{
                background:
                  "linear-gradient(90deg, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.55) 30%, rgba(0,0,0,0.25) 55%, rgba(0,0,0,0.02) 80%, rgba(0,0,0,0) 100%)",
              }}
            />
            {/* Mobile-only stronger overlay that extends further right */}
            <div
              className="absolute inset-0 md:hidden"
              style={{
                background:
                  "linear-gradient(90deg, rgba(16, 16, 16, 0.8) 0%, rgba(0,0,0,0.6) 40%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)",
              }}
            />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.08),transparent_30%),radial-gradient(circle_at_80%_10%,rgba(255,255,255,0.05),transparent_28%)]" />
          </div>
        </motion.div>

        <div className="max-w-7xl pl-8 md:pl-16 lg:pl-50 pr-4 md:pr-6 pt-55 pb-12">
          <motion.div className="max-w-3xl space-y-6 text-left" style={{ y: heroTextY }}>
            <motion.p
              className="text-xs md:text-sm uppercase tracking-[0.35em] text-white/70"
              initial={{ opacity: 0, y: 20 }}
              animate={heroInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 1 }}
            >
              Computer Science Undergraduate & Toastmaster
            </motion.p>

            <div className="space-y-2">
              <motion.h1
                className="text-5xl md:text-7xl font-light tracking-tight font-display text-white"
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={heroInView ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 20, scale: 0.95 }}
                transition={{ duration: 1.1, delay: 0.1 }}
              >
                Hi, I'm Dulain.
              </motion.h1>
            </div>

            <motion.div
              className="border-l border-white/25 pl-4 py-1 text-white/80 space-y-1 max-w-3xl dictionary-definition"
              initial={{ opacity: 0, x: -10 }}
              animate={heroInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -10 }}
              transition={{ duration: 1, delay: 0.35 }}
            >
              <p className="text-sm md:text-base italic">
                <span className="font-semibold text-white">Polymath</span> /ˈpɒl.i.mæθ/ (noun)
              </p>

              <p className="text-sm md:text-base">
                I am a 3rd year student at SLIIT pursuing BSc (Hons) in Computer Science, maintaining a 3.94 CGPA while serving as the president of Central Link Toastmasters Club. <br></br> <br></br>
                I live in three places at once: solving problems through code, being a die hard Toastmaster and figuring out Quantum Computing because, apparently regular computing wasn't strange enough.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={heroInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.9, delay: 0.75 }}
            >
              <Link
                to="/about"
                className="mt-6 inline-flex items-center gap-2 border border-white/35 bg-white/5 px-8 py-4 text-xs md:text-sm uppercase tracking-[0.25em] text-white transition hover:bg-white/10 hover:border-white/60"
              >
                Get to know me more
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* UNDER_CONSTRUCTION_START - remove this block */}
      {/* <AnimatePresence>
        {showUnderConstruction && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="max-w-3xl w-full rounded-3xl border border-white/15 bg-[#151520] shadow-[0_20px_60px_rgba(0,0,0,0.55)] p-8 md:p-10 text-center space-y-6">
              <p className="text-xs md:text-sm uppercase tracking-[0.35em] text-white/60">Temporary Notice</p>
              <h3 className="text-2xl md:text-4xl font-display text-white">
                still under construction guys!
              </h3>
              <p className="text-white/70 text-sm md:text-base">
                shipping updates daily! im basically a human barcode reader atp 😵‍💫
              </p>
              <div className="overflow-hidden border border-white/10 bg-black/30">
                <img
                  src={meme}
                  alt="digger"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence> */}
      {/* UNDER_CONSTRUCTION_END */}

      {/* 2 - Terminal Section */}
      <section
        ref={terminalRef}
        className="relative min-h-[60vh] flex items-center justify-center px-4 md:px-6 py-20 overflow-hidden bg-[#0c0c0c]"
      >
        {/* Background video */}
        <motion.video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover object-center opacity-30"
          style={{ scale: terminalVideoScale }}
        >
          <source src={terminalVideo} type="video/mp4" />
        </motion.video>

        {/* Dark overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/10" />

        {/* Content */}
        <motion.div
          className="relative z-10 max-w-5xl mx-auto text-center space-y-5"
          initial="hidden"
          whileInView="show"
          viewport={{ once: false, amount: 0.4 }}
          variants={{
            hidden: { opacity: 0 },
            show: { opacity: 1, transition: { staggerChildren: 0.18 } },
          }}
          style={{ filter: terminalTextBlur, opacity: terminalTextOpacity, scale: terminalTextScale }}
        >
          <motion.h2
            className="text-3xl md:text-6xl font-display text-white"
            variants={{
              hidden: { opacity: 0, y: 20 },
              show: { opacity: 1, y: 0, transition: { duration: 0.8 } },
            }}
          >
            fancy portfolio websites are overrated.
          </motion.h2>

          <motion.p
            className="text-base md:text-lg text-white/70"
            variants={{
              hidden: { opacity: 0, y: 20 },
              show: { opacity: 1, y: 0, transition: { duration: 0.8 } },
            }}
          >
            here's a Simple Interactive Terminal Environment (SITE) I made 👇🏻 ultimate nerd card
          </motion.p>

          {/* Centered Button */}
          <motion.div
            className="flex justify-center pt-2"
            variants={{
              hidden: { opacity: 0, y: 20 },
              show: { opacity: 1, y: 0, transition: { duration: 0.8 } },
            }}
          >
            <a
              href="https://site.realdulain.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border-2 border-white/50 bg-white/10 px-8 py-3 text-sm md:text-base font-semibold tracking-wider text-white transition hover:bg-white/20 hover:border-white/80 hover:scale-105 group"
            >
              try out SITE
              <svg
                className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 17L17 7M17 7H7M17 7V17" />
              </svg>
            </a>
          </motion.div>

          {/* Terminal Command Block - Bun Style */}
          <motion.div
            className="max-w-2xl mx-auto"
            variants={{
              hidden: { opacity: 0, y: 20 },
              show: { opacity: 1, y: 0, transition: { duration: 0.8 } },
            }}
          >
            <div className="rounded-2xl relative group border border-white/10 bg-white/5 backdrop-blur-sm overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-2 border-b border-white/10">
                <div className="flex items-center gap-2 text-white/60">
                  <svg
                    className="h-3.5 w-3.5"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zM4 18V6h16v12H4zm2-9h2v2H6V9zm0 3h2v2H6v-2zm3-3h9v2H9V9zm0 3h9v2H9v-2z" />
                  </svg>
                  <span className="text-xs font-medium">terminal</span>
                </div>
                <button
                  onClick={() => navigator.clipboard.writeText('curl realdulain.com')}
                  className="h-[26px] w-[26px] flex items-center justify-center rounded-md hover:bg-white/10 transition-colors group/copy"
                  aria-label="Copy command"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    className="text-white/40 group-hover/copy:text-white/60"
                  >
                    <path
                      d="M14.25 5.25H7.25C6.14543 5.25 5.25 6.14543 5.25 7.25V14.25C5.25 15.3546 6.14543 16.25 7.25 16.25H14.25C15.3546 16.25 16.25 15.3546 16.25 14.25V7.25C16.25 6.14543 15.3546 5.25 14.25 5.25Z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M2.80103 11.998L1.77203 5.07397C1.61003 3.98097 2.36403 2.96397 3.45603 2.80197L10.38 1.77297C11.313 1.63397 12.19 2.16297 12.528 3.00097"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>

              {/* Command */}
              <div className="px-4 py-4 font-mono text-sm bg-[#282a36]">
                <code className="text-white/90">
                  <span className="text-green-400">curl</span>
                  <span className="text-white/90"> realdulain.com</span>
                </code>
              </div>
            </div>

            {/* Small text below */}
            <p className="text-xs md:text-sm text-white mt-2">
              try this in your terminal 😉
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* 02 - Featured Projects */}
      <section
        ref={projectsRef}
        className="relative py-24 md:py-32 px-4 md:px-6 bg-[#0c0c0c]"
      >
        <motion.div className="absolute inset-0 z-0" style={{ scale: colorBendsScale }}>
          {/* Top fade overlay - smooth transition from terminal section */}
          <div
            className="absolute inset-x-0 h-48 z-[1] pointer-events-none"
            style={{
              background: "linear-gradient(to bottom, #0c0c0c 0%, transparent 100%)",
            }}
          />

          {/* Bottom fade overlay - smooth transition to next section */}
          <div
            className="absolute bottom-0 left-0 right-0 h-32 z-[1] pointer-events-none"
            style={{
              background: "linear-gradient(to top, #0c0c0c 0%, transparent 100%)",
            }}
          />

          {/* Dark overlay to tone down brightness */}
          <div className="absolute inset-0 bg-black/20 z-[1] pointer-events-none" />

          <ColorBends
            className="w-full h-full"
            colors={["#ff5c7a", "#8a5cff", "#00ffd1"]}
            rotation={0}
            speed={0.2}
            scale={1.1}
            frequency={1}
            warpStrength={1}
            mouseInfluence={1}
            parallax={0.5}
            noise={0.22}
            transparent
            autoRotate={0}
          />
        </motion.div>

        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            className="space-y-10"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            variants={{
              hidden: { opacity: 0 },
              show: { opacity: 1, transition: { staggerChildren: 0.16 } },
            }}
          >
            <motion.div
              className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-10"
              variants={{
                hidden: { opacity: 0, y: 16 },
                show: { opacity: 1, y: 0, transition: { duration: 0.8 } },
              }}
            >
              <div className="space-y-3">
                <p className="text-xs md:text-sm uppercase tracking-[0.35em] text-white/60">02 — Featured Projects</p>
                <h2 className="text-3xl md:text-5xl font-display text-white">Projects I've worked on</h2>
              </div>
              <a
                href="https://github.com/notdulain"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm md:text-base text-white/70 underline underline-offset-4 hover:text-white transition"
              >
                View all →
              </a>
            </motion.div>

            <motion.div
              variants={{
                hidden: { opacity: 0, y: 16 },
                show: { opacity: 1, y: 0, transition: { duration: 0.8 } },
              }}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 pt-8">
                {featuredProjects.map((project, idx) => (
                  <div key={idx} className="group relative flex flex-col gap-6">
                    {/* Image - Sharp, Cinematic */}
                    <a
                      href={project.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block overflow-hidden relative aspect-[16/9] bg-white/5 border-2 border-white/10 transition-colors duration-500 group-hover:border-white/30"
                    >
                      <img
                        src={project.image}
                        alt={project.name}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 filter grayscale-[20%] group-hover:grayscale-0"
                      />
                      {/* Subtle Overlay */}
                      <div className="absolute inset-0 bg-black/10 group-hover:bg-transparent transition-colors duration-500" />
                    </a>

                    {/* Content - Minimalist Editorial */}
                    <div className="flex flex-col gap-3">
                      <div className="flex justify-between items-baseline border-b border-white/10 pb-3">
                        <h3 className="text-3xl md:text-4xl font-display text-white group-hover:text-white/90 transition-colors">
                          {project.name}
                        </h3>
                        <span className="font-mono text-xs text-white/40">0{idx + 1}</span>
                      </div>

                      <p className="text-white/90 text-sm md:text-base leading-relaxed max-w-full">
                        {project.description}
                      </p>

                      {/* Tech Stack - Terminal Style */}
                      <div className="flex flex-wrap gap-x-2 text-xs font-mono text-white/40 uppercase tracking-wider mt-1">
                        {project.stack.map((stackItem, i) => (
                          <span key={i}>
                            {i > 0 && <span className="text-white/20 mr-2">/</span>}
                            {stackItem}
                          </span>
                        ))}
                      </div>

                      {/* Action - Text Link */}
                      <a
                        href={project.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="self-start mt-4 text-xs font-bold uppercase tracking-[0.2em] text-white/50 hover:text-white transition-colors"
                      >
                        View Project →
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <RecentUpdatesSection />

      {/* 04 - Say Hi + Footer */}
      <FooterSection />
    </div>
  );
}
